/**
 * Comprehensive data validation utilities for questionnaire data
 */

import { parseDateForCompare, startOfLocalDay } from './dateUtils';

/**
 * Validate and sanitize questionnaire data before submission
 * @param {Object} formData - The form data to validate
 * @returns {Object} - Validated and sanitized data
 * @throws {Error} - If validation fails
 */
export const validateQuestionnaireData = (formData) => {
  const errors = [];
  const warnings = [];

  // Helper function to get value from organized data structure
  const getValueFromOrganizedData = (data, fieldName) => {
    // Check if data is organized (has nested structure)
    if (data.basicInformation || data.parkVisitPlans || data.attractionRideStrategy) {
      // Map field names to their organized locations
      const fieldMapping = {
        fullName: data.basicInformation?.fullName,
        email: data.basicInformation?.email,
        phoneNumber: data.basicInformation?.phoneNumber,
        startDate: data.basicInformation?.startDate,
        endDate: data.basicInformation?.endDate,
        adults: data.groupInfo?.adults,
        kids: data.groupInfo?.kids,
        numBoys: data.groupInfo?.numBoys,
        numGirls: data.groupInfo?.numGirls,
        agesOfBoys: data.groupInfo?.agesOfBoys,
        agesOfGirls: data.groupInfo?.agesOfGirls,
        parks: data.parkVisitPlans?.parks,
        mkDays: data.parkVisitPlans?.mkDays,
        hsDays: data.parkVisitPlans?.hsDays,
        epcotDays: data.parkVisitPlans?.epcotDays,
        akDays: data.parkVisitPlans?.akDays,
        tlDays: data.parkVisitPlans?.tlDays,
        bbDays: data.parkVisitPlans?.bbDays,
        ticketType: data.parkTicketAccess?.ticketType,
        hasParkHopper: data.parkTicketAccess?.hasParkHopper,
        rideStrategy: data.attractionRideStrategy?.rideStrategy,
        showStrategy: data.showFireworkStrategy?.showStrategy,
        mustRides: data.attractionRideStrategy?.mustRides,
        mustSeeShows: data.showFireworkStrategy?.mustSeeShows,
        mustTryRestaurants: data.diningPreferences?.mustTryRestaurants,
        allergies: data.groupInfo?.allergies
      };
      
      return fieldMapping[fieldName] !== undefined ? fieldMapping[fieldName] : data[fieldName];
    }
    
    // Return direct access for flat data
    return data[fieldName];
  };

  // Required fields validation
  const requiredFields = ['fullName', 'email', 'startDate', 'endDate', 'adults'];
  for (const field of requiredFields) {
    const value = getValueFromOrganizedData(formData, field);
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errors.push(`${field} is required`);
    }
  }

  // Email validation
  const email = getValueFromOrganizedData(formData, 'email');
  if (email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      errors.push('Invalid email format');
    }
  }

  // Phone number validation
  const phoneNumber = getValueFromOrganizedData(formData, 'phoneNumber');
  if (phoneNumber) {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      warnings.push('Phone number format may be invalid');
    }
  }

  // Date validation (canonical parsing for comparisons)
  const startDate = getValueFromOrganizedData(formData, 'startDate');
  const endDate = getValueFromOrganizedData(formData, 'endDate');
  if (startDate && endDate) {
    const start = parseDateForCompare(startDate);
    const end = parseDateForCompare(endDate);
    const today = startOfLocalDay(new Date());

    if (!start || !end) {
      errors.push('Invalid date format');
    } else if (start < today) {
      errors.push('Start date cannot be in the past');
    } else if (end < start) {
      errors.push('End date must be after start date');
    } else if (end < today) {
      errors.push('End date cannot be in the past');
    }
  }

  // Number validation
  const numberFields = ['adults', 'kids', 'numBoys', 'numGirls'];
  for (const field of numberFields) {
    const value = getValueFromOrganizedData(formData, field);
    if (value !== undefined && value !== null && value !== '') {
      const num = parseInt(value);
      if (isNaN(num) || num < 0) {
        errors.push(`${field} must be a positive number`);
      }
      if (num > 20) {
        errors.push(`${field} cannot exceed 20`);
      }
    }
  }

  // Age validation
  const agesOfBoys = getValueFromOrganizedData(formData, 'agesOfBoys');
  if (agesOfBoys) {
    for (const age of agesOfBoys) {
      const num = parseInt(age);
      if (isNaN(num) || num < 0 || num > 18) {
        errors.push('Boy ages must be between 0 and 18');
        break;
      }
    }
  }

  const agesOfGirls = getValueFromOrganizedData(formData, 'agesOfGirls');
  if (agesOfGirls) {
    for (const age of agesOfGirls) {
      const num = parseInt(age);
      if (isNaN(num) || num < 0 || num > 18) {
        errors.push('Girl ages must be between 0 and 18');
        break;
      }
    }
  }

  // Park days validation
  const parkFields = ['mkDays', 'hsDays', 'epcotDays', 'akDays', 'tlDays', 'bbDays'];
  let totalParkDays = 0;
  for (const field of parkFields) {
    const value = getValueFromOrganizedData(formData, field);
    if (value) {
      const num = parseInt(value);
      if (isNaN(num) || num < 0 || num > 10) {
        errors.push(`${field} must be between 0 and 10`);
      } else {
        totalParkDays += num;
      }
    }
  }

  // Trip length validation
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const tripLength = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const usableDays = tripLength - 2; // Account for arrival/departure

    if (totalParkDays > usableDays) {
      // Check for park hopping access in both organized and flat data structures
      const ticketType = getValueFromOrganizedData(formData, 'ticketType');
      const hasParkHopper = getValueFromOrganizedData(formData, 'hasParkHopper');
      const isAnnualPass = ticketType === 'Annual Pass' || ticketType === 'AP';
      const isHopper = hasParkHopper === 'Yes';
      
      // When park hopping is allowed, we can have more park days than trip days
      // because multiple parks can be visited in a single day
      if (!isAnnualPass && !isHopper) {
        errors.push(`You've selected ${totalParkDays} park days, but only ${usableDays} of your ${tripLength}-day trip are available due to arrival and departure. Consider adding Park Hopper to visit multiple parks per day.`);
      } else {
        warnings.push(`You've selected ${totalParkDays} park days for a ${tripLength}-day trip. This is possible with Park Hopper or Annual Pass as you can visit multiple parks per day.`);
      }
    }
  }

  // Array field validation
  const arrayFields = ['parks', 'lightningLane', 'rideStrategy', 'mustRides', 'showStrategy', 'mustSeeShows', 'restaurantStrategy', 'mustTryRestaurants', 'diningTime', 'allergies', 'parkStartTime', 'breakTime'];
  for (const field of arrayFields) {
    if (formData[field] && !Array.isArray(formData[field])) {
      errors.push(`${field} must be an array`);
    }
  }

  // String length validation
  const stringFields = {
    fullName: 100,
    email: 100,
    phoneNumber: 20,
    ticketType: 100,
    hasParkHopper: 10,
    transportation: 100,
    stayingOnProperty: 100,
    resort: 100,
    otherHotel: 100,
    diningPlanInterest: 10,
    celebration: 200,
    hasDisability: 10,
    disabilities: 500,
    additionalInfo: 1000
  };

  for (const [field, maxLength] of Object.entries(stringFields)) {
    if (formData[field] && typeof formData[field] === 'string' && formData[field].length > maxLength) {
      errors.push(`${field} is too long (max ${maxLength} characters)`);
    }
  }

  // Throw error if validation fails
  if (errors.length > 0) {
    throw new Error(`Validation failed: ${errors.join(', ')}`);
  }

  // Return validated data with warnings
  return {
    data: formData,
    warnings: warnings.length > 0 ? warnings : null
  };
};

/**
 * Validate data types and structure with detailed error messages
 * @param {Object} data - Data to validate
 * @returns {Object} - Validation result with isValid and specific error messages
 */
export const validateDataTypesWithMessages = (data) => {
  const errors = [];
  
  // Helper function to get value from organized data structure
  const getValueFromOrganizedData = (data, fieldName) => {
    // Check if data is organized (has nested structure)
    if (data.basicInformation || data.parkVisitPlans || data.attractionRideStrategy) {
      // Map field names to their organized locations
      const fieldMapping = {
        fullName: data.basicInformation?.fullName,
        email: data.basicInformation?.email,
        phoneNumber: data.basicInformation?.phoneNumber,
        startDate: data.basicInformation?.startDate,
        endDate: data.basicInformation?.endDate,
        adults: data.groupInfo?.adults,
        kids: data.groupInfo?.kids,
        numBoys: data.groupInfo?.numBoys,
        numGirls: data.groupInfo?.numGirls,
        agesOfBoys: data.groupInfo?.agesOfBoys,
        agesOfGirls: data.groupInfo?.agesOfGirls,
        parks: data.parkVisitPlans?.parks,
        mustRides: data.attractionRideStrategy?.mustRides,
        mustSeeShows: data.showFireworkStrategy?.mustSeeShows || data.showFireworkStrategy?.showStrategy,
        mustTryRestaurants: data.diningPreferences?.mustTryRestaurants,
        allergies: data.diningPreferences?.allergies || data.groupInfo?.allergies
      };
      
      return fieldMapping[fieldName] !== undefined ? fieldMapping[fieldName] : data[fieldName];
    }
    
    // Return direct access for flat data
    return data[fieldName];
  };

  // Field name mapping for user-friendly error messages
  const fieldDisplayNames = {
    fullName: 'Full Name',
    email: 'Email Address',
    phoneNumber: 'Phone Number',
    startDate: 'Trip Start Date',
    endDate: 'Trip End Date',
    adults: 'Number of Adults',
    kids: 'Number of Children',
    mustRides: 'Must-Ride Attractions',
    mustTryRestaurants: 'Must-Try Restaurants'
  };

  const typeChecks = [
    { field: 'fullName', type: 'string', required: true },
    { field: 'email', type: 'string', required: true },
    { field: 'phoneNumber', type: 'string', required: true },
    { field: 'startDate', type: 'string', required: true },
    { field: 'endDate', type: 'string', required: true },
    { field: 'adults', type: 'number', required: true },
    { field: 'kids', type: 'number', required: false },
    { field: 'mustRides', type: 'array', required: true },
    { field: 'mustTryRestaurants', type: 'array', required: true }
  ];

  for (const check of typeChecks) {
    const value = getValueFromOrganizedData(data, check.field);
    const displayName = fieldDisplayNames[check.field] || check.field;
    
    if (check.required && (value === undefined || value === null || value === '')) {
      errors.push(`Please provide your ${displayName.toLowerCase()}. This information is required to create your personalized itinerary.`);
    }
    
    if (value !== undefined && value !== null && value !== '') {
      if (check.type === 'number' && typeof value !== 'number') {
        const parsedValue = parseInt(value);
        if (isNaN(parsedValue)) {
          errors.push(`Please enter a valid number for ${displayName.toLowerCase()}.`);
        }
      }
      if (check.type === 'string' && typeof value !== 'string') {
        errors.push(`Please enter valid text for ${displayName.toLowerCase()}.`);
      }
      if (check.type === 'array' && !Array.isArray(value)) {
        errors.push(`Please make selections for ${displayName.toLowerCase()}.`);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors: errors
  };
};

/**
 * Validate data types and structure (legacy function for backward compatibility)
 * @param {Object} data - Data to validate
 * @returns {boolean} - True if valid
 */
export const validateDataTypes = (data) => {
  const result = validateDataTypesWithMessages(data);
  return result.isValid;
};

/**
 * Check for data consistency issues
 * @param {Object} data - Data to check
 * @returns {Array} - Array of consistency warnings
 */
export const checkDataConsistency = (data) => {
  // Helper function to get value from organized data structure
  const getValueFromOrganizedData = (data, fieldName) => {
    // Check if data is organized (has nested structure)
    if (data.basicInformation || data.parkVisitPlans || data.attractionRideStrategy) {
      // Map field names to their organized locations
      const fieldMapping = {
        fullName: data.basicInformation?.fullName,
        email: data.basicInformation?.email,
        phoneNumber: data.basicInformation?.phoneNumber,
        startDate: data.basicInformation?.startDate,
        endDate: data.basicInformation?.endDate,
        adults: data.groupInfo?.adults,
        kids: data.groupInfo?.kids,
        numBoys: data.groupInfo?.numBoys,
        numGirls: data.groupInfo?.numGirls,
        agesOfBoys: data.groupInfo?.agesOfBoys,
        agesOfGirls: data.groupInfo?.agesOfGirls,
        parks: data.parkVisitPlans?.parks,
        mkDays: data.parkVisitPlans?.mkDays,
        hsDays: data.parkVisitPlans?.hsDays,
        epcotDays: data.parkVisitPlans?.epcotDays,
        akDays: data.parkVisitPlans?.akDays,
        tlDays: data.parkVisitPlans?.tlDays,
        bbDays: data.parkVisitPlans?.bbDays,
        rideStrategy: data.attractionRideStrategy?.rideStrategy,
        showStrategy: data.showFireworkStrategy?.showStrategy,
        mustRides: data.attractionRideStrategy?.mustRides || data.mustRides,
        mustSeeShows: data.showFireworkStrategy?.mustSeeShows || data.mustSeeShows,
        mustTryRestaurants: data.diningPreferences?.mustTryRestaurants || data.mustTryRestaurants,
        allergies: data.groupInfo?.allergies
      };
      
      return fieldMapping[fieldName] !== undefined ? fieldMapping[fieldName] : data[fieldName];
    }
    
    // Return direct access for flat data
    return data[fieldName];
  };

  const warnings = [];

  // ============================================================================
  // NOTE: Children distribution is treated as approximate/heuristic data.
  // We don't require exact matches to avoid blocking families with valid use cases.
  // ============================================================================
  // Check if number of children matches age arrays (non-blocking informational warning)
  const kids = getValueFromOrganizedData(data, 'kids');
  const agesOfBoys = getValueFromOrganizedData(data, 'agesOfBoys');
  const agesOfGirls = getValueFromOrganizedData(data, 'agesOfGirls');
  
  if (kids && agesOfBoys && agesOfGirls) {
    const totalAges = (agesOfBoys?.length || 0) + (agesOfGirls?.length || 0);
    if (totalAges !== kids && totalAges > 0) {
      warnings.push('Children age information is approximate - itinerary will be personalized based on available data');
      console.info('[DataValidation] Approximate children age data', {
        expectedCount: kids,
        providedAges: totalAges
      });
    }
  }

  // Check if selected parks match park days
  const parks = getValueFromOrganizedData(data, 'parks');
  if (parks && parks.length > 0) {
    const parkFields = ['mkDays', 'hsDays', 'epcotDays', 'akDays', 'tlDays', 'bbDays'];
    let hasParkDays = false;
    for (const field of parkFields) {
      const value = getValueFromOrganizedData(data, field);
      if (value && parseInt(value) > 0) {
        hasParkDays = true;
        break;
      }
    }
    if (!hasParkDays) {
      warnings.push('Parks selected but no park days specified');
    }
  }

  // Check if ride strategy matches must rides
  const rideStrategy = getValueFromOrganizedData(data, 'rideStrategy');
  const mustRides = getValueFromOrganizedData(data, 'mustRides');
  if (rideStrategy && mustRides) {
    if (rideStrategy.includes('Not Interested') && mustRides.length > 0) {
      warnings.push('Ride strategy is "Not Interested" but must-rides are selected');
    }
  }

  // Check if show strategy matches must see shows
  const showStrategy = getValueFromOrganizedData(data, 'showStrategy');
  const mustSeeShows = getValueFromOrganizedData(data, 'mustSeeShows');
  if (showStrategy && mustSeeShows) {
    if (showStrategy.includes('Not Interested') && mustSeeShows.length > 0) {
      warnings.push('Show strategy is "Not Interested" but must-see shows are selected');
    }
  }

  return warnings;
};

/**
 * Validate data before localStorage storage
 * @param {Object} data - Data to validate
 * @returns {boolean} - True if valid for storage
 */
export const validateForStorage = (data) => {
  try {
    // Check if data can be serialized
    JSON.stringify(data);
    
    // Check for circular references
    const seen = new WeakSet();
    const checkCircular = (obj) => {
      if (obj && typeof obj === 'object') {
        if (seen.has(obj)) {
          return false;
        }
        seen.add(obj);
        for (const key in obj) {
          if (!checkCircular(obj[key])) {
            return false;
          }
        }
      }
      return true;
    };
    
    return checkCircular(data);
  } catch (error) {
    return false;
  }
};

/**
 * Comprehensive data validation pipeline
 * @param {Object} formData - Form data to validate
 * @returns {Object} - Validation result with isValid, data, errors, and warnings
 */
export const validateFormData = (formData) => {
  try {
    // Step 1: Type validation with detailed messages
    const typeValidation = validateDataTypesWithMessages(formData);
    if (!typeValidation.isValid) {
      return {
        isValid: false,
        errors: typeValidation.errors,
        warnings: []
      };
    }

    // Step 2: Storage validation
    if (!validateForStorage(formData)) {
      return {
        isValid: false,
        errors: ['Data cannot be safely stored'],
        warnings: []
      };
    }

    // Step 3: Comprehensive validation
    const validationResult = validateQuestionnaireData(formData);

    // Step 4: Consistency checks
    const consistencyWarnings = checkDataConsistency(formData);
    if (consistencyWarnings.length > 0) {
      validationResult.warnings = validationResult.warnings || [];
      validationResult.warnings.push(...consistencyWarnings);
    }

    return {
      isValid: true,
      data: validationResult.data,
      errors: validationResult.errors || [],
      warnings: validationResult.warnings || []
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [`Validation failed: ${error.message}`],
      warnings: []
    };
  }
}; 